import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import { Modal, ModalBody, Spinner } from 'reactstrap'
import { MdKeyboardArrowLeft } from 'react-icons/md'
import { FiEdit } from 'react-icons/fi'
import eventApi from '_api/event'
import { changeNamePackingList } from '_redux/modules/event'
import '../style.scss'

export default function HeaderPackingList({
  packingList,
  setPackingList,
  summary,
  onBack,
  onExport,
  onExportExcel,
  onExportExcelSummary,
  onDelete,
  loadingExport,
}) {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const [name, setName] = useState(packingList?.name || '')
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const inputRef = useRef(null)
  const toggleExportModal = () => setIsExportModalOpen((prev) => !prev)

  useEffect(() => {
    setName(packingList?.name || '')
  }, [packingList?.name])

  const handleUpdateName = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setName(packingList?.name || '')
      return
    }
    if (trimmedName === packingList?.name) {
      return
    }
    try {
      await eventApi.changeNamePackingList(packingList.id, { packingListName: trimmedName })
      dispatch(changeNamePackingList(packingList.id, trimmedName))
      setPackingList({
        ...packingList,
        name: trimmedName,
      })
      addToast('Success', { appearance: 'success', autoDismiss: true })
    } catch ({ msgResp }) {
      addToast(msgResp || 'Something went wrong', { appearance: 'error', autoDismiss: true })
      setName(packingList?.name || '')
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur()
    }
    if (e.key === 'Escape') {
      setName(packingList?.name || '')
      // blur after the reset is committed so handleUpdateName sees the original name
      setTimeout(() => inputRef.current?.blur())
    }
  }

  const pluralize = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

  const focusInput = () => {
    inputRef.current?.focus()
  }

  return (
    <div className='packing-list-new-header'>
      <div className='breadcrumb-packing-list' onClick={onBack}>
        <MdKeyboardArrowLeft size={14} />
        Packing Lists
      </div>
      <div className='detail-header'>
        <div className='detail-title-block'>
          <div className='detail-title-wrap' onClick={focusInput}>
            <span className='detail-title-sizer' data-value={name || ' '}>
              <input
                ref={inputRef}
                className='detail-title'
                id='detail-title-input'
                value={name}
                size={1}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleUpdateName}
                onKeyDown={handleKeyDown}
                title='Click to rename'
              />
            </span>
            <button type='button' className='edit-title-btn' aria-label='Rename packing list'>
              <FiEdit size={15} />
            </button>
          </div>
          {summary && (
            <div className='detail-summary'>
              {pluralize(summary.events, 'event')} · {pluralize(summary.buyers, 'buyer')} ·{' '}
              {pluralize(summary.items, 'item')}
            </div>
          )}
        </div>
        <div className='detail-actions'>
          <button className='btn-outline' onClick={onExport} disabled={loadingExport !== null}>
            {loadingExport !== null ? (
              <Spinner size='sm' style={{ color: '#009A97' }} />
            ) : (
              <svg
                width='13'
                height='13'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='7 10 12 15 17 10' />
                <line x1='12' y1='15' x2='12' y2='3' />
              </svg>
            )}
            {loadingExport !== null ? `Exporting (${loadingExport}%)` : 'Export PDF'}
          </button>
          <button className='btn-outline' onClick={toggleExportModal}>
            <svg
              width='13'
              height='13'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
              <polyline points='7 10 12 15 17 10' />
              <line x1='12' y1='15' x2='12' y2='3' />
            </svg>
            Export Excel
          </button>
          <button className='btn-danger' onClick={onDelete} aria-label='Delete packing list'>
            <svg
              width='13'
              height='13'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='3 6 5 6 21 6' />
              <path d='M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' />
            </svg>
            <span className='btn-label'>Delete</span>
          </button>
        </div>
      </div>

      <Modal
        isOpen={isExportModalOpen}
        toggle={toggleExportModal}
        centered
        className='export-excel-modal'
      >
        <ModalBody>
          <h5 className='export-excel-modal-title'>Choose Excel format</h5>
          <div className='export-excel-modal-options'>
            <button
              type='button'
              className='export-excel-option'
              onClick={() => {
                onExportExcel()
                toggleExportModal()
              }}
            >
              <svg
                width='22'
                height='22'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M8 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7l-4-4h-2' />
                <path d='M8 3v6h6' />
                <path d='M8 15h8' />
                <path d='M8 19h8' />
              </svg>
              <span className='export-excel-option-title'>By Buyer</span>
              <span className='export-excel-option-desc'>1 sheet per buyer, split across tabs</span>
            </button>
            <button
              type='button'
              className='export-excel-option'
              onClick={() => {
                onExportExcelSummary()
                toggleExportModal()
              }}
            >
              <svg
                width='22'
                height='22'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <rect x='3' y='3' width='18' height='18' rx='2' />
                <line x1='3' y1='9' x2='21' y2='9' />
                <line x1='3' y1='15' x2='21' y2='15' />
                <line x1='9' y1='9' x2='9' y2='21' />
              </svg>
              <span className='export-excel-option-title'>Summary</span>
              <span className='export-excel-option-desc'>All buyers in 1 long sheet</span>
            </button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  )
}
